import { ADD_CHAT_MESSAGE, API_CALL, SET_CHAT_MESSAGES, SET_CHAT_OUTAGE_MESSAGE, SET_CURRENT_CHAT_ID, UPDATE_CHAT, UPDATE_CHAT_TITLE, SET_LAST_UPDATED_AT, SET_UNREAD_MESSAGE_COUNT } from 'actions/actionConstants'
import { AppAction } from 'actions/types/ActionTypes'
import { CREATE_NEW_CHAT, FETCH_CHAT_MESSAGES, FETCH_CHAT_WELCOME_MESSAGE, FETCH_CHATS, POST_MESSAGE_FEEDBACK } from 'actions/apiActionConstants'
import { createChat, createChatApi, getChatMessages, getChats, getChatWelcomeMessage, postFeedback, putServerChat, sendChatMessage } from 'api/supportAssistant'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { chatActions } from 'supportAssistant/constants/chatActions'
import { selectCurrentChat } from 'supportAssistant/selectors/selectCurrentChat'
import { buildChatMessagePayload } from 'supportAssistant/utils/messageUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getOrderBookingNumbers, getOrdersWithAirportTransfers, getUpcomingOrders } from 'selectors/orderSelectors'
import { globalSocket } from 'supportAssistant/hooks/useSocket'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getLuxPlusSupportContactNumber, getLoyaltySupportContactNumber } from 'selectors/supportContactSelector'
import { isHotelOrder } from 'lib/order/orderUtils'
import { isCurrentOrCloseToCheckIn } from 'lib/order/isUpcoming'
import { fetchOffersById } from 'actions/OfferActions'
import getIsLoyaltyPrioritySupportNumberEnabled from 'loyalty/selectors/getIsLoyaltyPrioritySupportNumberEnabled'
import getLoyaltyAccountTier from 'loyalty/selectors/getLoyaltyAccountTier'

export function fetchChats(upcoming: Array<App.Order>, chatDeepLink: App.ChatDeepLinks): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    const {
      geo: { currentRegionCode: regionCode },
    } = state

    const customerId = getCurrentUserId(state)
    const account = state.auth.account
    const currentChat = selectCurrentChat(state)
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const hasLoyaltyPrioritySupport = getIsLoyaltyPrioritySupportNumberEnabled(state)
    const loyaltyAccountTier = getLoyaltyAccountTier(state)

    const upcomingHotelOrders = upcoming.filter(order => isHotelOrder(order)).map(({ id, items }) => ({
      id,
      itemCount: items.length,
      orderType: 'hotel',
    }))

    const upcomingHotelOfferIds = upcoming.flatMap(order => order.items.map(item => item.offerId))
    if (upcomingHotelOfferIds.length > 0) {
      await dispatch(fetchOffersById(upcomingHotelOfferIds))
    }

    const orderBookingNumbers = getOrderBookingNumbers(state)
    const ordersWithAirportTransfers = getOrdersWithAirportTransfers(state)

    if (!customerId) return

    dispatch({
      type: API_CALL,
      api: FETCH_CHATS,
      request: async() => {
        // If transitioning from logged out to logged in, add CustomerId to current chat.
        if (globalSocket && currentChat && !currentChat.customerId) {
          if (currentChat.id === 'default') {
            await createChat(regionCode, customerId, account.givenName, canViewLuxPlusBenefits, hasLoyaltyPrioritySupport, loyaltyAccountTier, orderBookingNumbers, ordersWithAirportTransfers)
          } else {
            await putServerChat({ id: currentChat.id, customerId })
          }
        }

        if (chatDeepLink === 'list-orders' && upcomingHotelOrders.length > 0) {
          const payload: App.ChatApi = {
            customerName: account.givenName,
            customerId,
            upcomingOrders: upcomingHotelOrders,
            isLuxPlusMember: canViewLuxPlusBenefits,
            chatDeepLink,
            regionCode,
            isCurrentOrder: upcoming.every(isCurrentOrCloseToCheckIn),
          }

          await createChatApi(payload)
        }

        const chats = await getChats()

        // chat always come from the server in descing order
        let currentChatId = chats.length > 0 ? chats[0].id : undefined

        const activeChat = chats.find((chat) => chat.status === 'ACTIVE')
        if (!activeChat) {
          const [newChat] = await createChat(regionCode, customerId, account.givenName, canViewLuxPlusBenefits, hasLoyaltyPrioritySupport, loyaltyAccountTier, orderBookingNumbers, ordersWithAirportTransfers)
          chats.push(newChat)
          currentChatId = newChat.id
        }

        return {
          chats,
          currentChatId,
        }
      },
    })
  }
}

export function createNewChat(messages: Array<Partial<App.ChatMessage>> = EmptyArray): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const regionCode = state.geo.currentRegionCode
    const customerId = getCurrentUserId(state)
    const account = state.auth.account
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const hasLoyaltyPrioritySupport = getIsLoyaltyPrioritySupportNumberEnabled(state)
    const loyaltyAccountTier = getLoyaltyAccountTier(state)
    const orderBookingNumbers = getOrderBookingNumbers(state)

    dispatch({
      type: API_CALL,
      api: CREATE_NEW_CHAT,
      request: async() => {
        const [newChat, newMessages] = await createChat(regionCode, customerId, account.givenName, canViewLuxPlusBenefits, hasLoyaltyPrioritySupport, loyaltyAccountTier, orderBookingNumbers)

        messages.forEach(message => sendChatMessage({ ...message, chatId: newChat.id }))
        return {
          chat: newChat,
          messages: [...newMessages, ...(messages as Array<App.ChatMessage>)],
        }
      },
    })
  }
}

export function fetchChatWelcomeMessage(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const regionCode = state.geo.currentRegionCode
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const hasLoyaltyPrioritySupport = getIsLoyaltyPrioritySupportNumberEnabled(state)
    const loyaltyAccountTier = getLoyaltyAccountTier(state)

    return dispatch({
      type: API_CALL,
      api: FETCH_CHAT_WELCOME_MESSAGE,
      request: () => getChatWelcomeMessage(regionCode, canViewLuxPlusBenefits, hasLoyaltyPrioritySupport, loyaltyAccountTier),
    })
  }
}

export function setCurrentChatId(chatId: string) {
  return {
    type: SET_CURRENT_CHAT_ID,
    chatId,
  }
}

export function setChatOutageMessage(error: string | undefined) {
  return {
    type: SET_CHAT_OUTAGE_MESSAGE,
    error,
  }
}

export function fetchChatMessages(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const currentChat = selectCurrentChat(state)

    if (currentChat) {
      dispatch({
        type: API_CALL,
        api: FETCH_CHAT_MESSAGES,
        chatId: currentChat.id,
        request: async() => {
          const messages = await getChatMessages(currentChat.id)
          return {
            messages,
          }
        },
      })
    }
  }
}

export function postMessageFeedback(feedback: App.ChatMessageFeedback): AppAction {
  return {
    type: API_CALL,
    api: POST_MESSAGE_FEEDBACK,
    feedback,
    request: async() => {
      const response = await postFeedback(feedback)

      if (response) {
        showSnackbar(
          'Thank you for your valuable feedback. It greatly helps us improve the user experience.',
          'success',
          { heading: 'Feedback successfully submitted' },
        )
      }
      return response
    },
  }
}

export function addChatMessage(message: string, chatMessageMeta?: App.ChatMessageMeta): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const hasLoyaltyPrioritySupport = getIsLoyaltyPrioritySupportNumberEnabled(state)
    const loyaltyAccountTier = getLoyaltyAccountTier(state)
    const luxPlusContact = getLuxPlusSupportContactNumber(state)
    const loyaltyContact = getLoyaltySupportContactNumber(state)

    const currentChatId = state.supportAssistant.currentChatId ?? 'default'
    const customerId = getCurrentUserId(state)
    const account = state.auth.account
    const upcoming = getUpcomingOrders(state)
    const isCurrentOrder = upcoming.length === 1 && upcoming.every(isCurrentOrCloseToCheckIn)
    const orderBookingNumbers = getOrderBookingNumbers(state)
    const ordersWithAirportTransfers = getOrdersWithAirportTransfers(state)
    const upcomingOrdersWithAirportTransfers = ordersWithAirportTransfers.filter(orderWithAirportTransfer => upcoming.find(upcomingOrder => upcomingOrder.id === orderWithAirportTransfer.id))

    const messageMetaPayload = buildChatMessagePayload({
      chatMessageMeta,
      currentChatId,
      account,
      customerId,
      hasUpcomingBooking: !!upcoming.length,
      canViewLuxPlusBenefits,
      hasLoyaltyPrioritySupport,
      loyaltyAccountTier,
      luxPlusContact,
      loyaltyContact,
      message,
      isCurrentOrder,
      orderBookingNumbers,
      ordersWithAirportTransfers,
      upcomingOrdersWithAirportTransfers,
    }) as App.ChatMessage

    if (currentChatId === 'default') {
      dispatch(createNewChat([messageMetaPayload]))
    } else {
      sendChatMessage(messageMetaPayload)
    }

    dispatch({
      type: ADD_CHAT_MESSAGE,
      message: messageMetaPayload,
      chatId: currentChatId,
    })

    // trigger customer portal actions for certain chat actions.  I.e. create Genesys chat.
    const messageAction = chatMessageMeta?.action ? chatActions[chatMessageMeta.action] : undefined
    if (messageAction && messageMetaPayload.meta?.payload) {
      messageAction(messageMetaPayload.meta.payload)
    }
  }
}

export function addChatResponse(message: App.ChatMessage, callback = (_response: { ok: boolean; id: string }): void => {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    // block the duplicate welcome message coming through.
    const messageAlreadyExists = state.supportAssistant.messages[message.chatId!]?.some(mess => mess.id === message.id)
    if (messageAlreadyExists) {
      return
    }

    if (callback) {
      callback({ ok: true, id: message.id })
    }

    dispatch({
      type: ADD_CHAT_MESSAGE,
      message,
      chatId: message.chatId!,
    })
  }
}

export function updateChatTitle(chatTitle: App.ChatTitle): AppAction {
  return {
    type: UPDATE_CHAT_TITLE,
    chatId: chatTitle.chatId,
    title: chatTitle.title,
  }
}

export function updateChat(chat: App.Chat): AppAction {
  return {
    type: UPDATE_CHAT,
    chat,
  }
}

export function setUnreadCount(count: number): AppAction {
  return {
    type: SET_UNREAD_MESSAGE_COUNT,
    count,
  }
}

export function setLastUpdatedAt(lastUpdatedAt: string): AppAction {
  return {
    type: SET_LAST_UPDATED_AT,
    lastUpdatedAt,
  }
}

export function setChatMessages(chatId: string, messages: Array<App.ChatMessage>): AppAction {
  return {
    type: SET_CHAT_MESSAGES,
    chatId,
    messages,
  }
}
